'use client';

import { Button } from '@mage-ui/components';
import {
	button,
	courseCard,
	flexWrapper,
	gridCol,
	section,
} from '@mage-ui/styled-system/recipes';
import { useContext, useState } from 'react';
import { StaticTextContext } from '@/components/context/StaticTextProvider';
import { CourseCard } from '@/components/ui/data-display/cards/course-card/CourseCard';
import { CourseDetails } from '@/components/ui/data-display/course-details/CourseDetails';
import { Feedback } from '@/components/ui/data-display/feedback/Feedback';
import { AlertItem } from '@/components/ui/feedback/alert-item/AlertItem';
import { PriceTypeEnum } from '@/components/ui/forms/form-online-enrollment/enums';
import { GridCol } from '@/components/ui/layouts/grids/GridCol';
import { GridRow } from '@/components/ui/layouts/grids/GridRow';
import { Section } from '@/components/ui/layouts/sections/Section';
import { useRouter } from '@/i18n/routing';
import { createRedsysForm } from '@/services/client/redsys-service';
import { request } from '@/services/server/request';
import { formatPercent, formatPrice } from '../../../../utils/amount';
import { parseDateString } from '../../../../utils/dates';

export const EnrollmentSummary = ({
	formData,
	locale,
	enrollment,
	course,
	group,
	language,
}) => {
	const { st } = useContext(StaticTextContext);
	const subsidyApplied = (enrollment.subsidy?.percentage || 0) / 100;
	const [hasCanceled, setHasCanceled] = useState(false);
	const [error, setError] = useState('');
	const router = useRouter();

	const infoData = [
		{
			name: st('forms.enrollment.feedback.summary.level'),
			value: group.fields.level,
			key: 'info-data-1',
		},
		{
			name: st('forms.enrollment.feedback.summary.price'),
			value: formatPrice(enrollment.cost),
			key: 'info-data-2',
		},
		{
			name: st('forms.enrollment.feedback.summary.start_date'),
			value: parseDateString(course.startDate),
			key: 'info-data-3',
		},
		{
			name: st('forms.enrollment.feedback.summary.end_date'),
			value: parseDateString(course.endDate),
			key: 'info-data-4',
		},
		{
			name: st('forms.enrollment.feedback.summary.schedule'),
			value: group?.fields?.schedule,
			key: 'info-data-5',
		},
	];

	const createEnrollment = async () => {
		const response = await request('/api/enrollment', 'POST', {
			...formData,
			group_id: group?.fields?.id_backend,
		});

		const createdEnrollment = response?.data?.body?.enrollment;

		if (!createdEnrollment) {
			setError(response.data?.details);
			return null;
		}

		return response?.data?.body?.enrollment;
	};

	const onPay = async () => {
		if (!enrollment?.id) {
			enrollment = await createEnrollment();

			if (!enrollment) {
				return;
			}
		}

		await redirectToRedsys({ enrollmentId: enrollment.id });
	};

	const redirectToRedsys = async ({ enrollmentId }) => {
		const {
			data: { body },
			status,
		} = await request('/api/payment', 'POST', {
			enrollment_id: enrollmentId,
			amount: enrollment.cost,
		});

		if (status === 200 && body?.success) {
			return router.push(body.success);
		}

		const redsysData = body;

		if (status !== 200 || !redsysData || !redsysData.Ds_MerchantParameters) {
			return;
		}

		createRedsysForm(redsysData).submit();
	};

	return hasCanceled ? (
		<Feedback
			language={locale}
			data={{
				fields: {
					title: st('forms.enrollment.feedback.canceled.title'),
					content: st('forms.enrollment.feedback.canceled.content'),
					cta_text: st('forms.enrollment.feedback.canceled.cta_text'),
				},
			}}
		/>
	) : (
		<Section
			className={section({ align: 'start' })}
			heading={{
				title: st('forms.enrollment.feedback.summary.title'),
			}}
			footer={
				<div
					className={flexWrapper({
						align: 'center',
						gap: 'gap400',
						wrap: 'wrap',
					})}
				>
					<Button
						onClick={() => setHasCanceled(true)}
						classNames={{ root: button({ variant: 'outlined' }) }}
					>
						{st('forms.enrollment.feedback.summary.cancel_button')}
					</Button>
					<Button onClick={onPay}>
						{st('forms.enrollment.feedback.summary.submit_button')}
					</Button>
				</div>
			}
		>
			{error && <AlertItem text={error} />}

			<GridRow>
				<GridCol
					className={gridCol({
						colSpanM: '5',
						colSpanL: '4',
					})}
				>
					<CourseCard
						className={courseCard({ color: 'neutral' })}
						data={{
							title: course.type.name,
							name: language.fields.name,
							iso: language.fields.iso,
							infoData: infoData,
						}}
					/>
				</GridCol>
				<GridCol
					className={gridCol({
						colSpanS: '8',
						colSpanM: '6',
						colSpanL: '4',
					})}
				>
					<CourseDetails
						data={{
							title: st('forms.enrollment.feedback.summary.subtitle'),
							course: course.name,
							...(course.price?.type === PriceTypeEnum.subsidized && {
								course_price: formatPrice(course.price.baseAmount),
								material_price: formatPrice(course.price.materialAmount),
								subsidized_price: formatPrice(
									course.price.subsidizableAmount * (1 - subsidyApplied),
								),
								course_discount: formatPercent(subsidyApplied),
							}),
							final_price: formatPrice(enrollment.cost),
						}}
						texts={{
							discountApplied: st(
								'forms.enrollment.feedback.summary.discount_applied',
							),
							coursePrice: st('forms.enrollment.feedback.summary.course_price'),
							materialPrice: st(
								'forms.enrollment.feedback.summary.material_price',
							),
							subsidizedPrice: st(
								'forms.enrollment.feedback.summary.subsidized_price',
							),
							finalPrice: st('forms.enrollment.feedback.summary.final_price'),
						}}
					/>
				</GridCol>
			</GridRow>
		</Section>
	);
};
